import { useEffect, useState, type CSSProperties } from 'react';
import type { MapObjectDTO, OperationPlanDTO } from '@warplan/shared';
import type { IconCategory } from '@warplan/shared';
import {
  useOperationPlanSource as useOperationPlan,
  useUpdateOperationPlanSource as useUpdateOperationPlan,
  useObjectsSource as useMapObjects,
  useDeleteObjectSource as useDeleteMapObject,
} from '../lib/map-data-source.js';
import { useOperationPlanPanel } from '../state/operation-plan-panel-store.js';
import {
  PRIORITY_SLOTS,
  ATTACK_SLOT_COUNT,
  DEFEND_SLOT_COUNT,
  DEFEND_SLOT_OFFSET,
} from '../lib/priority-sync.js';
import { getIconEntry } from '../lib/icon-catalog.js';

export interface OperationPlanPanelProps {
  /** When false, every control is read-only. */
  canEdit: boolean;
}

const PANEL_WIDTH = 320;
const containerOpenStyle: CSSProperties = { width: PANEL_WIDTH };

export function OperationPlanPanel({
  canEdit,
}: OperationPlanPanelProps): React.JSX.Element {
  const isOpen = useOperationPlanPanel((s) => s.isOpen);
  const toggle = useOperationPlanPanel((s) => s.toggle);
  const query = useOperationPlan();
  const updateMut = useUpdateOperationPlan();
  const objectsQuery = useMapObjects();
  const deleteObjectMut = useDeleteMapObject();

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={toggle}
        title="Show operation plan panel"
        aria-label="Show operation plan panel"
        className="absolute right-2 top-2 z-30 flex h-10 w-10 items-center justify-center rounded border border-orange-700 bg-slate-900/90 text-orange-200 shadow-lg backdrop-blur transition hover:bg-slate-700"
      >
        <span aria-hidden="true">◀</span>
      </button>
    );
  }

  const dto = query.data ?? null;
  const disabled = !canEdit || updateMut.isPending;

  return (
    <aside
      role="complementary"
      aria-label="Operation plan"
      className="absolute right-2 top-2 bottom-2 z-30 flex flex-col rounded border border-orange-700 bg-slate-900/95 text-slate-100 shadow-lg backdrop-blur"
      style={containerOpenStyle}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-orange-700 px-3 py-2">
        <span className="text-sm font-semibold text-orange-200">Plan d'opération</span>
        <button
          type="button"
          onClick={toggle}
          title="Replier le panneau"
          aria-label="Collapse operation plan panel"
          className="flex h-7 w-7 items-center justify-center rounded text-slate-300 transition hover:bg-slate-700 hover:text-slate-100"
        >
          <span aria-hidden="true">▶</span>
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {query.isLoading && <p className="text-base text-slate-400">Chargement…</p>}
        {query.isError && (
          <p className="text-base text-red-400">
            Impossible de charger le plan d'opération.
          </p>
        )}
        {dto && (
          <OperationPlanForm
            dto={dto}
            objects={objectsQuery.data?.objects ?? []}
            disabled={disabled}
            onPatch={(patch) => updateMut.mutate(patch)}
            onDeleteObject={(id) => deleteObjectMut.mutate(id)}
          />
        )}
      </div>
    </aside>
  );
}

interface OperationPlanFormProps {
  dto: OperationPlanDTO;
  objects: ReadonlyArray<MapObjectDTO>;
  disabled: boolean;
  onPatch: (patch: Partial<OperationPlanDTO>) => void;
  onDeleteObject: (id: string) => void;
}

function OperationPlanForm({
  dto,
  objects,
  disabled,
  onPatch,
  onDeleteObject,
}: OperationPlanFormProps): React.JSX.Element {
  const type = dto.objectiveType;

  // Slice the flat priorities array into attack and defense halves,
  // keeping the global slot index alongside the text so the priority badge
  // and edit/delete callbacks target the correct slot (even when some
  // intermediate slots are empty).
  const attackPriorities: ReadonlyArray<{ slot: number; text: string }> = dto.priorities
    .slice(0, ATTACK_SLOT_COUNT)
    .map((text, i) => ({ slot: i, text }))
    .filter((p) => p.text.trim() !== '');
  const defensePriorities: ReadonlyArray<{ slot: number; text: string }> = dto.priorities
    .slice(DEFEND_SLOT_OFFSET, DEFEND_SLOT_OFFSET + DEFEND_SLOT_COUNT)
    .map((text, i) => ({ slot: DEFEND_SLOT_OFFSET + i, text }))
    .filter((p) => p.text.trim() !== '');

  function commitSlot(slot: number, text: string): void {
    const next = [...dto.priorities];
    while (next.length <= slot) next.push('');
    const trimmed = text.trim();
    if ((next[slot] ?? '') === trimmed) return;
    next[slot] = trimmed;
    onPatch({ priorities: next });
  }

  function clearSlot(slot: number): void {
    // Delete every map icon mapped to this slot so the priority-sync hook
    // does not push the icon's note back into the plan after we clear it.
    const slotDef = PRIORITY_SLOTS[slot];
    if (slotDef) {
      for (const obj of objects) {
        if (obj.metadata.iconId === slotDef.iconId) {
          onDeleteObject(obj.id);
        }
      }
    }
    if (!(dto.priorities[slot] ?? '').trim()) return;
    const next = [...dto.priorities];
    next[slot] = '';
    onPatch({ priorities: next });
  }

  return (
    <div className="flex flex-col gap-3">
      <NumberField
        label="Numéro de guerre"
        value={dto.warNumber}
        disabled={disabled}
        onCommit={(next) => {
          if (next !== dto.warNumber) onPatch({ warNumber: next });
        }}
      />
      <TextField
        label="Titre"
        value={dto.title}
        maxLength={120}
        disabled={disabled}
        onCommit={(next) => {
          if (next !== dto.title) onPatch({ title: next });
        }}
      />
      <DateField
        label="Date"
        value={dto.date}
        disabled={disabled}
        onCommit={(next) => {
          if (next !== dto.date) onPatch({ date: next });
        }}
      />
      <ObjectiveTypeSelector
        value={dto.objectiveType}
        disabled={disabled}
        onChange={(next) => {
          if (next !== dto.objectiveType) onPatch({ objectiveType: next });
        }}
      />
      {/* --- Objective text field(s) --- */}
      {type !== 'defense' && (
        <TextAreaField
          label={type === 'mixed' ? 'Objectif attaque' : 'Objectif'}
          icon={<ObjectiveIcon kind="attack" />}
          value={dto.objective}
          maxLength={2000}
          disabled={disabled}
          onCommit={(next) => {
            if (next !== dto.objective) onPatch({ objective: next });
          }}
        />
      )}
      {type !== 'attack' && (
        <TextAreaField
          label={type === 'mixed' ? 'Objectif défense' : 'Objectif'}
          icon={<ObjectiveIcon kind="defense" />}
          value={dto.objectiveDefense}
          maxLength={2000}
          disabled={disabled}
          onCommit={(next) => {
            if (next !== dto.objectiveDefense) onPatch({ objectiveDefense: next });
          }}
        />
      )}
      {/* --- Priority list(s) --- */}
      {type !== 'defense' && (
        <PrioritiesList
          label={type === 'mixed' ? 'Priorités attaque' : 'Priorités'}
          priorities={attackPriorities}
          disabled={disabled}
          onCommitSlot={commitSlot}
          onClearSlot={clearSlot}
        />
      )}
      {type !== 'attack' && (
        <PrioritiesList
          label={type === 'mixed' ? 'Priorités défense' : 'Priorités'}
          priorities={defensePriorities}
          disabled={disabled}
          onCommitSlot={commitSlot}
          onClearSlot={clearSlot}
        />
      )}
      <p className="pt-1 text-right text-[12px] text-slate-500">
        Mis à jour : {new Date(dto.updatedAt).toLocaleString()}
      </p>
    </div>
  );
}

function FieldLabel({
  children,
  icon,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}): React.JSX.Element {
  return (
    <label className="flex items-center gap-1.5 px-1 text-[13px] uppercase tracking-wide text-slate-300">
      {icon}
      <span>{children}</span>
    </label>
  );
}

/** PNG icon for the objective field based on kind (attack/defense). */
function ObjectiveIcon({ kind }: { kind: 'attack' | 'defense' }): React.JSX.Element {
  const entry = kind === 'attack'
    ? getIconEntry('op_attack', 'OPERATION_ATTACK_MAIN_OBJECTIVE')
    : getIconEntry('op_defend', 'OPERATION_DEFEND_MAIN_OBJECTIVE');
  return (
    <img
      src={entry.src}
      alt=""
      width={16}
      height={16}
      style={{ flexShrink: 0, objectFit: 'contain' }}
    />
  );
}

/**
 * Segmented control to pick Attack / Defense / Mixed as the operation's
 * objective type. Uses the real PNG icons from the catalog.
 */
type ObjectiveType = 'attack' | 'defense' | 'mixed';

const OBJECTIVE_TYPE_OPTIONS: ReadonlyArray<{
  value: ObjectiveType;
  kind: IconCategory;
  iconId: string;
  label: string;
  activeClass: string;
}> = [
  {
    value: 'attack',
    kind: 'op_attack',
    iconId: 'OPERATION_ATTACK_MAIN_OBJECTIVE',
    label: 'Attaque',
    activeClass: 'border-red-700 bg-red-900/40 text-red-200',
  },
  {
    value: 'defense',
    kind: 'op_defend',
    iconId: 'OPERATION_DEFEND_MAIN_OBJECTIVE',
    label: 'Défense',
    activeClass: 'border-blue-600 bg-blue-900/40 text-blue-200',
  },
  {
    value: 'mixed',
    kind: 'op_attack',
    iconId: 'OPERATION_ATTACK_MAIN_OBJECTIVE',
    label: 'Mixte',
    activeClass: 'border-orange-600 bg-orange-900/40 text-orange-200',
  },
];

interface ObjectiveTypeSelectorProps {
  value: ObjectiveType;
  disabled: boolean;
  onChange: (next: ObjectiveType) => void;
}

function ObjectiveTypeSelector({
  value,
  disabled,
  onChange,
}: ObjectiveTypeSelectorProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>Type d'objectif</FieldLabel>
      <div className="flex gap-1" role="group" aria-label="Type d'objectif">
        {OBJECTIVE_TYPE_OPTIONS.map((opt) => {
          const isActive = value === opt.value;
          const attackEntry = getIconEntry('op_attack', 'OPERATION_ATTACK_MAIN_OBJECTIVE');
          const defendEntry = getIconEntry('op_defend', 'OPERATION_DEFEND_MAIN_OBJECTIVE');
          const iconEntry = opt.value !== 'mixed'
            ? getIconEntry(opt.kind, opt.iconId)
            : null;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              aria-pressed={isActive}
              onClick={() => onChange(opt.value)}
              title={opt.label}
              aria-label={opt.label}
              className={[
                'flex flex-1 flex-col items-center gap-1 rounded border px-1 py-1.5 text-[11px] transition',
                isActive
                  ? opt.activeClass
                  : 'border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700',
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              ].join(' ')}
            >
              {opt.value === 'mixed' ? (
                <span className="flex gap-0.5">
                  <img src={attackEntry.src} alt="" width={20} height={20} style={{ objectFit: 'contain' }} />
                  <img src={defendEntry.src} alt="" width={20} height={20} style={{ objectFit: 'contain' }} />
                </span>
              ) : (
                <img src={iconEntry!.src} alt="" width={22} height={22} style={{ objectFit: 'contain' }} />
              )}
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Renders the real operation objective PNG (`OPERATION_ATTACK_OBJECTIVE_*` /
 * `OPERATION_DEFEND_OBJECTIVE_*`) for the given priority slot.
 */
function PriorityBadge({ index }: { index: number }): React.JSX.Element {
  const slot = PRIORITY_SLOTS[index];
  if (!slot) return <span style={{ width: 26, height: 26, flexShrink: 0 }} />;
  const entry = getIconEntry(slot.kind, slot.iconId);
  return (
    <img
      src={entry.src}
      alt={entry.label}
      width={26}
      height={26}
      style={{ flexShrink: 0, objectFit: 'contain' }}
    />
  );
}

interface TextFieldProps {
  label: string;
  value: string;
  maxLength: number;
  disabled: boolean;
  onCommit: (next: string) => void;
}

function TextField({
  label,
  value,
  maxLength,
  disabled,
  onCommit,
}: TextFieldProps): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="text"
        value={draft}
        maxLength={maxLength}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onCommit(draft.trim())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          else if (e.key === 'Escape') setDraft(value);
        }}
        className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100 outline-none focus:border-orange-500 disabled:opacity-60"
      />
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  value: number;
  disabled: boolean;
  onCommit: (next: number) => void;
}

function NumberField({
  label,
  value,
  disabled,
  onCommit,
}: NumberFieldProps): React.JSX.Element {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="number"
        min={0}
        max={1_000_000}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const n = Number.parseInt(draft, 10);
          if (Number.isFinite(n) && n >= 0) onCommit(n);
          else setDraft(String(value));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          else if (e.key === 'Escape') setDraft(String(value));
        }}
        className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100 outline-none focus:border-orange-500 disabled:opacity-60"
      />
    </div>
  );
}

interface DateFieldProps {
  label: string;
  value: string;
  disabled: boolean;
  onCommit: (next: string) => void;
}

function DateField({
  label,
  value,
  disabled,
  onCommit,
}: DateFieldProps): React.JSX.Element {
  // Convert any ISO datetime to YYYY-MM-DD for <input type="date">.
  const inputValue = /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      <input
        type="date"
        value={inputValue}
        disabled={disabled}
        onChange={(e) => onCommit(e.target.value)}
        style={{ colorScheme: 'dark' }}
        className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100 outline-none focus:border-orange-500 disabled:opacity-60"
      />
    </div>
  );
}

interface TextAreaFieldProps {
  label: string;
  icon?: React.ReactNode;
  value: string;
  maxLength: number;
  disabled: boolean;
  onCommit: (next: string) => void;
}

function TextAreaField({
  label,
  icon,
  value,
  maxLength,
  disabled,
  onCommit,
}: TextAreaFieldProps): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel icon={icon}>{label}</FieldLabel>
      <textarea
        value={draft}
        maxLength={maxLength}
        disabled={disabled}
        rows={4}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onCommit(draft.trim())}
        className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100 outline-none focus:border-orange-500 disabled:opacity-60"
      />
    </div>
  );
}

interface PrioritiesListProps {
  label: string;
  /** Non-empty entries with their absolute slot index in PRIORITY_SLOTS. */
  priorities: ReadonlyArray<{ slot: number; text: string }>;
  disabled: boolean;
  onCommitSlot: (slot: number, text: string) => void;
  onClearSlot: (slot: number) => void;
}

function PrioritiesList({
  label,
  priorities,
  disabled,
  onCommitSlot,
  onClearSlot,
}: PrioritiesListProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{label}</FieldLabel>
      {priorities.length === 0 ? (
        <p className="text-xs text-slate-500 italic">
          Placez des icônes de priorité sur la carte pour les voir apparaître ici.
        </p>
      ) : (
        <ol className="flex flex-col gap-1">
          {priorities.map((p) => (
            <PriorityItem
              key={p.slot}
              slot={p.slot}
              text={p.text}
              disabled={disabled}
              onCommit={(next) => onCommitSlot(p.slot, next)}
              onClear={() => onClearSlot(p.slot)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

interface PriorityItemProps {
  slot: number;
  text: string;
  disabled: boolean;
  onCommit: (next: string) => void;
  onClear: () => void;
}

function PriorityItem({
  slot,
  text,
  disabled,
  onCommit,
  onClear,
}: PriorityItemProps): React.JSX.Element {
  const [draft, setDraft] = useState(text);
  useEffect(() => setDraft(text), [text]);
  return (
    <li className="flex items-center gap-2 rounded border border-slate-700 bg-slate-800/70 px-2 py-1 text-sm">
      <PriorityBadge index={slot} />
      <input
        type="text"
        value={draft}
        disabled={disabled}
        maxLength={500}
        title={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const trimmed = draft.trim();
          if (trimmed === text.trim()) return;
          if (!trimmed) {
            onClear();
          } else {
            onCommit(trimmed);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          else if (e.key === 'Escape') {
            setDraft(text);
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-sm text-slate-100 outline-none focus:border-orange-500 focus:bg-slate-900 disabled:opacity-60"
      />
      <button
        type="button"
        disabled={disabled}
        onClick={onClear}
        title="Supprimer la note"
        aria-label="Supprimer la note"
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 transition hover:bg-red-900/40 hover:text-red-300 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
      >
        <span aria-hidden="true">✕</span>
      </button>
    </li>
  );
}
